import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Fuel, Clock, Gauge, Leaf, TrendingDown } from "lucide-react";
import { CALENDLY } from "@/lib/api";
import { openExternal } from "@/lib/openExternal";

const POINTS = [
  { icon: Fuel, label: "Audit current diesel burn" },
  { icon: Gauge, label: "Model load profile & peaks" },
  { icon: TrendingDown, label: "Benchmark MBESS vs genset TCO" },
  { icon: Leaf, label: "Quantify CO₂ you'd avoid" },
  { icon: Clock, label: "Engineer visit within 10 days" },
];

export default function SiteAssessment() {
  return (
    <section
      className="py-24 relative overflow-hidden border-y border-tratou-green/20"
      data-testid="site-assessment-cta"
    >
      <div className="absolute inset-0 -z-10 bg-grid bg-grid-fade opacity-50" />
      <div className="absolute -top-24 -right-24 w-[500px] h-[500px] bg-tratou-green/5 blur-[120px] rounded-full -z-10" />

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="eyebrow">Mobile Battery Energy Storage · MBESS</span>
          <h2 className="heading-section mt-5">
            Reduce Diesel Dependence. <br />
            <span className="text-tratou-green">Power Your Site Smarter.</span>
          </h2>
          <p className="mt-6 text-tratou-dim text-lg max-w-xl">
            Explore mobile battery power for construction and industrial operations. Assess your
            current generator costs, understand your charging requirements, and discover whether
            MBESS is right for your site.
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/assessment" className="btn-primary" data-testid="assessment-cta-primary">
              Get Your Free Diesel Generator Assessment <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href={CALENDLY}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => openExternal(e, CALENDLY)}
              className="btn-secondary"
              data-testid="assessment-cta-demo"
            >
              Book a Live Demo
            </a>
          </div>
          <p className="mt-4 text-xs text-tratou-faint max-w-xl">
            Savings and suitability depend on the site load, charging source, utilisation, and operating conditions.
          </p>
        </motion.div>

        <motion.ul
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="panel divide-y divide-white/5"
          data-testid="assessment-points"
        >
          {POINTS.map(({ icon: Icon, label }, i) => (
            <li key={label} className="flex items-center gap-4 p-5">
              <span className="w-10 h-10 border border-tratou-green/40 bg-tratou-void/70 flex items-center justify-center flex-shrink-0">
                <Icon className="w-4 h-4 text-tratou-green" />
              </span>
              <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-tratou-dim mr-2 w-6">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="text-white text-sm md:text-base">{label}</span>
            </li>
          ))}
        </motion.ul>
      </div>
    </section>
  );
}
